import {
  Datagrid,
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  ReferenceManyField,
  usePermissions,
  SingleFieldList,
  ChipField,
  useDataProvider,
  useGetOne,
} from 'react-admin'
import { useRecordContext } from 'ra-core'
import get from 'lodash/get'
import { useParams } from 'react-router-dom'
import { useEffect, useState, useMemo } from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  CircularProgress,
} from '@mui/material'
import userPermissions from '../Users/userPermissions'
import VendorFormLayout from '../components/VendorFormLayout'

// Define the form section type
interface FormField {
  label: string
  source: string
  required: boolean
}

interface FormSection {
  title: string
  fields: FormField[]
}

const ReferenceVendorTagsField = (props) => {
  const { source } = props
  const record = useRecordContext(props)
  const value = get(record, source)
  return (
    <div>
      <div className="border border-stone-200/50 rounded-md overflow-hidden">
        <table className="table-auto table w-full">
          <thead className="py-1">
            <tr className="text-left text-sm">
              <th className="py-2 pr-3 pl-2 font-semibold">
                Tag category name
              </th>
              <th className="py-2 pl-3 pr-6 font-semibold">Name</th>
            </tr>
          </thead>
          <tbody>
            {value.map((tag) => (
              <tr
                key={tag.id}
                className="text-sm border-b border-stone-200/50 active cursor-pointer hover:bg-stone-100"
              >
                <td className="py-2 pr-3 pl-2 capitalize">
                  {tag.tag_category}
                </td>
                <td className="py-3 pl-3 pr-6 border-l border-stone-200/50">
                  {tag.tag}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const VendorShow = () => {
  const { permissions } = usePermissions()
  const { id } = useParams()
  const dataProvider = useDataProvider()
  const { data: record, isLoading } = useGetOne('vendors', { id })

  // State to store the form sections
  const [formSections, setFormSections] = useState<FormSection[]>([])
  const [loading, setLoading] = useState(true)

  let user: any = localStorage.getItem('user')
  if (user) {
    user = JSON.parse(user)
  }

  // Load the organisation's vendor form configuration
  useEffect(() => {
    const loadVendorForm = async () => {
      try {
        setLoading(true)
        const organisationId = record?.organisation?.id
        const endpoint = organisationId
          ? `vendor-forms/organisations/${organisationId}`
          : 'organisations/current/vendor-forms'

        const response = await dataProvider.custom(endpoint, {
          method: 'GET',
        })

        // Check if we got a list of vendor forms or a single vendor form
        if (Array.isArray(response.data)) {
          // We got a list of vendor forms, use the first one if available
          if (response.data.length > 0) {
            setFormSections(response.data[0].vendor_form || [])
          } else {
            // Use empty form sections if no vendor forms exist
            setFormSections([])
          }
        } else if (response.data?.vendor_form) {
          // We got a single vendor form
          setFormSections(response.data.vendor_form)
        } else {
          // Use empty form sections if no configuration is found
          setFormSections([])
        }
      } catch (error) {
        console.error('Error loading vendor form:', error)
        // Use empty form sections on error
        setFormSections([])
      } finally {
        setLoading(false)
      }
    }

    loadVendorForm()
  }, [dataProvider, record])

  // Prepare values for form fields based on tags
  const formValues = useMemo(() => {
    if (!record || !formSections.length) return {}

    // Create a map of source -> tag for quick lookup
    const sourceToTagMap = {}

    // Process all tags
    if (record.tags) {
      record.tags.forEach((tag) => {
        if (tag.tag_category?.name) {
          // Use the tag category name as the source
          sourceToTagMap[tag.tag_category.name] = tag
        }
      })
    }

    // For each form field, set its value if there's a matching tag
    const formData = {}
    formSections.forEach((section) => {
      section.fields.forEach((field) => {
        // If the field has source "name", use the vendor's name
        if (field.source === 'name') {
          formData[field.source] = record.name
        } else {
          const matchingTag = sourceToTagMap[field.source]
          if (matchingTag) {
            formData[field.source] = matchingTag.name
          }
        }
      })
    })

    return formData
  }, [record, formSections])

  // Filter tags that are already represented in form fields
  const filteredTags = useMemo(() => {
    if (!record || !record.tags || !formSections.length)
      return record?.tags ?? []

    // Get all sources from form sections
    const formSources = formSections
      .flatMap((section) => section.fields)
      .map((field) => field.source)

    // Filter out tags that are represented in form fields
    return record.tags.filter(
      (tag) =>
        !(tag.tag_category && formSources.includes(tag.tag_category.name))
    )
  }, [record, formSections])

  if (isLoading || loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Show>
      <SimpleShowLayout>
        <TextField source="name" emptyText="N/A" />

        <ReferenceManyField
          reference="vendor_names"
          target="vendor_id"
          label="Also known as"
        >
          {permissions & userPermissions.SUPER_ADMIN ? (
            <Datagrid bulkActionButtons={false}>
              <TextField source="name" />
              <TextField source="source" />
              <TextField source="priority" />
              <ReferenceVendorTagsField source="reference_vendor_coding" />
            </Datagrid>
          ) : (
            <SingleFieldList>
              <ChipField source="name" />
            </SingleFieldList>
          )}
        </ReferenceManyField>

        {user && [1, 4].includes(user.organisation_id) && (
          <TextField source="company_number" emptyText="N/A" />
        )}
        {user && [1, 4].includes(user.organisation_id) && (
          <TextField source="credit_score" emptyText="N/A" />
        )}
        {user && [1, 4].includes(user.organisation_id) && (
          <TextField source="address_line_1" emptyText="N/A" />
        )}
        {user && [1, 4].includes(user.organisation_id) && (
          <TextField source="post_code" emptyText="N/A" />
        )}
        {user && [1, 4].includes(user.organisation_id) && (
          <TextField source="company_category" emptyText="N/A" />
        )}
        {user && [1, 4].includes(user.organisation_id) && (
          <TextField source="company_status" emptyText="N/A" />
        )}
        {user && [1, 4].includes(user.organisation_id) && (
          <TextField source="origin_country" emptyText="N/A" />
        )}
        {user && [1, 4].includes(user.organisation_id) && (
          <DateField source="dissolution_date" emptyText="N/A" />
        )}
        {user && [1, 4].includes(user.organisation_id) && (
          <DateField source="change_of_name_date" emptyText="N/A" />
        )}
        {user && [1, 4].includes(user.organisation_id) && (
          <TextField source="previous_company_name" emptyText="N/A" />
        )}

        {/* Display form sections */}
        {formSections && formSections.length > 0 ? (
          <VendorFormLayout
            formSections={formSections.map((section) => ({
              ...section,
              fields: section.fields.map((field) => ({
                ...field,
                value: formValues[field.source],
              })),
            }))}
            readOnly
          />
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      bgcolor: '#0077c8',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '16px',
                      p: 1.5,
                    }}
                  >
                    Vendor Basic Information
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{
                      bgcolor: '#d9d9d9',
                      width: '35%',
                      p: 0.25,
                      textAlign: 'left',
                      verticalAlign: 'middle',
                    }}
                  >
                    Vendor Name
                  </TableCell>
                  <TableCell sx={{ p: 0.25 }}>
                    {record?.name || 'N/A'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Display remaining tags */}
        {filteredTags && filteredTags.length > 0 && (
          <Box mt={2}>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{
                        bgcolor: '#0077c8',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        p: 1.5,
                      }}
                    >
                      Organisation Tags
                    </TableCell>
                  </TableRow>
                  {filteredTags.map((tag) => (
                    <TableRow key={tag.id}>
                      <TableCell
                        sx={{
                          bgcolor: '#d9d9d9',
                          width: '35%',
                          p: 0.25,
                          textAlign: 'left',
                          verticalAlign: 'middle',
                        }}
                      >
                        {tag.tag_category?.name ?? 'N/A'}
                      </TableCell>
                      <TableCell sx={{ p: 0.25 }}>
                        {tag.name ?? 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </SimpleShowLayout>
    </Show>
  )
}

export default VendorShow
